import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { JobApplication, JobOpening } from '../types/api';
import RichTextDisplay from '../components/RichTextDisplay';
import { 
  ArrowLeftIcon,
  PencilIcon,
  DocumentArrowDownIcon,
  CheckIcon,
  XMarkIcon,
  StarIcon,
  UserIcon,
  BriefcaseIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';

const ApplicationDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [application, setApplication] = useState<JobApplication | null>(null);
  const [job, setJob] = useState<JobOpening | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (id) {
      fetchApplication();
    }
  }, [id]);

  const fetchApplication = async () => {
    try {
      setLoading(true);
      const appData = await apiService.getApplicationById(parseInt(id!));
      setApplication(appData);
      
      // Fetch job details
      const jobData = await apiService.getJobById(appData.job_id);
      setJob(jobData);
    } catch (error: any) {
      console.error('Failed to fetch application:', error);
      if (error.response?.status === 404) {
        setError('Application not found');
      } else {
        setError('Failed to load application details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!application) return;
    
    try {
      setUpdatingStatus(true);
      await apiService.updateApplication(application.id, { status: newStatus as any });
      await fetchApplication(); // Refresh the data
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDownloadResume = async () => {
    if (!application) return;
    
    try {
      const response = await apiService.downloadResume(application.id);
      // Create a blob and download it
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resume-${application.first_name}-${application.last_name}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download resume:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'shortlisted':
        return 'bg-blue-100 text-blue-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'selected':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <CalendarIcon className="h-4 w-4" />;
      case 'shortlisted':
        return <StarIcon className="h-4 w-4" />;
      case 'rejected':
        return <XMarkIcon className="h-4 w-4" />;
      case 'selected':
        return <CheckIcon className="h-4 w-4" />;
      default:
        return <CalendarIcon className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="text-center py-12">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Application Not Found</h1>
        <p className="text-gray-600 mb-6">{error || 'The application you\'re looking for doesn\'t exist.'}</p>
        <Link
          to="/backoffice/applications"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Applications
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Link
            to="/backoffice/applications"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mr-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Applications
          </Link>
        </div>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Application from {application.first_name} {application.last_name}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Applied on {new Date(application.created_at).toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(application.status)}`}>
              {getStatusIcon(application.status)}
              <span className="ml-1 capitalize">{application.status}</span>
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Motivation Letter */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Motivation Letter</h2>
            {application.motivation_letter ? (
              <div className="prose prose-sm max-w-none">
                <RichTextDisplay content={application.motivation_letter} />
              </div>
            ) : (
              <p className="text-gray-500 italic">No motivation letter provided.</p>
            )}
          </div>

          {/* Notes */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">Notes</h2>
              <Link
                to={`/backoffice/applications/${application.id}/edit?scroll=notes`}
                className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Edit Notes
              </Link>
            </div>
            {application.notes ? (
              <div className="prose prose-sm max-w-none">
                <RichTextDisplay content={application.notes} />
              </div>
            ) : (
              <p className="text-gray-500 italic">No notes added yet.</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Application Info */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Application Details</h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <UserIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {application.first_name} {application.last_name}
                  </p>
                  <p className="text-sm text-gray-500">Applicant</p>
                </div>
              </div>

              <div className="flex items-center">
                <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{application.email}</p>
                  <p className="text-sm text-gray-500">Email</p>
                </div>
              </div>

              {application.phone && (
                <div className="flex items-center">
                  <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{application.phone}</p>
                    <p className="text-sm text-gray-500">Phone</p>
                  </div>
                </div>
              )}

              <div className="flex items-center">
                <CalendarIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(application.created_at).toLocaleDateString()}
                  </p>
                  <p className="text-sm text-gray-500">Applied</p>
                </div>
              </div>

              {application.reviewed_at && (
                <div className="flex items-center">
                  <CalendarIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(application.reviewed_at).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-500">Last Reviewed</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Job Info */}
          {job && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Job Position</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <BriefcaseIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{job.title}</p>
                    <p className="text-sm text-gray-500">Position</p>
                  </div>
                </div>

                {job.location && (
                  <div>
                    <p className="text-sm font-medium text-gray-900">📍 {job.location}</p>
                    <p className="text-sm text-gray-500">Location</p>
                  </div>
                )}

                {job.salary_range && (
                  <div>
                    <p className="text-sm font-medium text-gray-900">💰 {job.salary_range}</p>
                    <p className="text-sm text-gray-500">Salary Range</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
            <div className="space-y-3">
              {/* Edit Application */}
              <Link
                to={`/backoffice/applications/${application.id}/edit`}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Application
              </Link>

              {/* Resume Download */}
              {application.resume_path && (
                <button
                  onClick={handleDownloadResume}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                  Download Resume
                </button>
              )}

              {/* Status Update */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Update Status</p>
                <div className="grid grid-cols-2 gap-2">
                  {['pending', 'shortlisted', 'rejected', 'selected'].map((status) => (
                    <button
                      key={status}
                      onClick={() => handleStatusUpdate(status)}
                      disabled={updatingStatus || application.status === status}
                      className={`px-3 py-2 text-xs font-medium rounded-md ${
                        application.status === status
                          ? 'bg-blue-100 text-blue-800 cursor-default'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } disabled:opacity-50`}
                    >
                      {updatingStatus ? 'Updating...' : status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetailsPage;
